const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/utils/fetchUtils.ts';
import * as Redux from 'redux';

import { Logger } from '@cistec/common_web/services/KisLoggerService';
import { getPatapiToken } from 'App/store/login';
import { PATAPI } from 'App/middleware/patapi';
import { IKisApiActionsMap } from '@cistec/common_web/defs/actionTypes';

const Log = Logger('fetchUtils');

/**
 * Fetches data from a PATAPI endpoint. No token is sent along with this type of request. Use this during authentication
 * only!
 */
export function fetchWithSpecialEndpointAndDispatch(
    endpoint: string,
    body: any,
    type: IKisApiActionsMap,
    meta: any,
    dispatch: Redux.Dispatch<any>,
    webToken?: string,
    // @ts-ignore
    additionalHeaders?: Headers
) {
    Promise.resolve()
        .then(() => {
            const action = { type: type.pending, meta };
            dispatch(action);
        })
        .catch(() => {
            Log.error('Failed to dispatch pending action!');
        })
        .then(() => {
            const headers = new Headers();
            headers.append('Content-Type', 'application/json; charset=utf-8');
            if (webToken) {
                headers.append('Authorization', 'Bearer ' + webToken);
            }

            const req = { method: 'POST', headers, body: JSON.stringify(body) };

            return fetch(endpoint, req);
        })
        .then((reply: any) => {
            return Promise.resolve()
                .then(() => (reply && reply.json()) || {})
                .catch(() => ({}))
                .then((payload: any) => {
                    if (reply.status && reply.status < 400) {
                        return {
                            type: type.success,
                            payload,
                            meta,
                        };
                    } else {
                        return {
                            type: type.failure,
                            payload: Object.assign({}, payload, { status: reply.status }),
                            meta,
                        };
                    }
                })
                .catch((err: any) => {
                    Log.error('Error while decoding success response!', err);
                });
        })
        .catch((reply: any) => {
            return Promise.resolve()
                .then(() => (reply && reply.json()) || {})
                .catch(() => ({}))
                .then((payload: any) => {
                    return {
                        type: type.failure,
                        payload: Object.assign({}, payload, { status: reply.status }),
                        meta,
                    };
                })
                .catch((err: any) => {
                    Log.error('Error while decoding failure response!', err);
                });
        })
        .then((action: any) => {
            if (!action.type) {
                throw Error(`Action type missing!`);
            } else {
                dispatch(action);
            }
        })
        .catch((err: any) => {
            Log.error('Error while dispatching action! ', err);
        });
}

export enum HttpMethod {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    PATCH = 'PATCH',
    DELETE = 'DELETE',
    OPTIONS = 'OPTIONS',
    HEAD = 'HEAD',
    TRACE = 'TRACE',
}

export const createPatapiCall = (
    type: IKisApiActionsMap,
    endpoint: string,
    method: HttpMethod = HttpMethod.GET,
    meta?: any,
    body?: any
) => {
    const token = getPatapiToken();

    if (endpoint.startsWith('/')) {
        endpoint = endpoint.slice(1);
    }

    return {
        type: [type.pending, type.success, type.failure],
        [PATAPI]: {
            isNoAuth: false,
            isExternalApi: true,
            endpoint: `/api/${endpoint}`,
            requestConfig: {
                method,
                headers: {
                    Accept: 'application/json',
                    Authorization: token,
                    'Content-Type': 'application/json',
                },
                ...(body && { body }),
            },
        },
        ...(meta && { meta }),
    };
};

export const createNoAuthPatapiCall = (
    type: IKisApiActionsMap | undefined,
    endpoint: string,
    method: HttpMethod,
    meta?: any,
    body?: any
) => {
    if (endpoint.startsWith('/')) {
        endpoint = endpoint.slice(1);
    }
    return {
        ...(!type ? undefined : { type: [type.pending, type.success, type.failure] }),
        [PATAPI]: {
            isNoAuth: true,
            isExternalApi: true,
            endpoint: `/api/${endpoint}`,
            requestConfig: {
                method,
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                ...(body && { body }),
            },
        },
        ...(meta && { meta }),
    };
};
